import { type ActiveMatch, type NavItem } from './navTree'

export type Location = {
  pathname: string
  search: string
  /** Current locale, used to ignore the optional `/:locale` URL prefix when matching. */
  locale?: string
}

function readLocale(): string | undefined {
  // @ts-expect-error window.locale is injected by the Rails layout
  return typeof window !== 'undefined' ? window.locale : undefined
}

// Routes carry an optional `/:locale` prefix, so the same page can be reached at
// `/groups/43` or `/en/groups/43`. Nav hrefs are always generated with the
// locale, so strip a leading `/<locale>` segment from both the current path and
// the match prefix before comparing — then matching works regardless of whether
// the live URL includes the prefix.
function withoutLocale(path: string, locale?: string): string {
  if (!locale) return path
  const prefix = `/${locale}`
  if (path === prefix) return '/'
  return path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path
}

export function isItemActive(item: NavItem, location: Location): boolean {
  return item.activeMatch?.some((match) => matches(match, location)) ?? false
}

function matches(match: ActiveMatch, location: Location): boolean {
  const pathname = withoutLocale(location.pathname, location.locale)
  const pathPrefix = withoutLocale(match.pathPrefix, location.locale)
  const pathOk = match.exact
    ? pathname === pathPrefix
    : pathname === pathPrefix || pathname.startsWith(`${pathPrefix}/`)
  if (!pathOk) return false

  if (match.query) {
    const params = new URLSearchParams(location.search)
    return Object.entries(match.query).every(([key, value]) => params.get(key) === value)
  }

  return true
}

export function currentLocation(): Location {
  return { pathname: window.location.pathname, search: window.location.search, locale: readLocale() }
}

export function locationFromUrl(url: string): Location {
  const { pathname, search } = new URL(url, 'http://relative')
  return { pathname, search, locale: readLocale() }
}
